<script setup lang="ts">
// vue
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

// stores
import { Call, CallExpanded, useEntityStore } from '@/modules/investing/stores/better-entity-store'
import { Invoice } from '@/modules/investing/types'
import { useCallStore } from '@/modules/investing/stores/call-store'
import { useInvoiceStore } from '@/modules/investing/stores/invoice-store'

// composables
import { useModal } from '@/modules/shared/composables/use-modal'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'

// utils
import { format } from '@/modules/shared/utils/v-table'
import { initialMoney, toNumber } from '@/modules/shared/utils/money'
import { formatCurrency } from '@/modules/shared/utils/currency'

// shared components
import { VButton, VModal } from '@/modules/shared/components'
import DescriptionList from '@/modules/shared/components/DescriptionList.vue'
import StackedChartSummary from '@/modules/shared/components/StackedChartSummary.vue'
import VBadge from '@/modules/shared/components/VBadge.vue'
import VLoading from '@/modules/shared/components/v-loading.vue'
import { createQueryString } from '@/modules/shared/utils/routing'
import { useExtendedI18n } from '@/i18n'
import { investorPath } from '@/modules/investing/utils/investor'
import InvoiceBadge from '../../InvoiceBadge.vue'
import CallPaymentForm from './CallPaymentForm.vue'

const route = useRoute()
const { n } = useExtendedI18n()

interface Props {
  callId: string
  status?: string
  isAdmin?: boolean
  isScheduled?: boolean
  isOpenByDefault?: boolean
  onOpenDetails?: any
  refetch?: () => Promise<void>
}

const props = defineProps<Props>()

const { slug } = useRoute().params as { slug: string }

const entityStore = useEntityStore()
const store = useCallStore()
const invoiceStore = useInvoiceStore()

const wiringInstructionsSection = ref<HTMLElement | null>(null)
const focusedOn = ref<string | null>(null)

onMounted(async () => {
  if (props.isOpenByDefault) {
    await fetchAndOpenModal()
  }
  if (route.query.call_section === 'wiring-instructions') {
    wiringInstructionsSection.value?.scrollIntoView({ behavior: 'smooth' })
    focusedOn.value = 'wiring-instructions'
  }

  setTimeout(() => {
    focusedOn.value = null
  }, 1000)
})

const call = ref<Call>({} as Call)
const invoice = ref<Invoice>({} as Invoice)
const expandedCall = computed((): CallExpanded => {
  if (!call.value) return null
  return {
    ...call.value,
    shareholder: entityStore.getShareholder(call.value.shareholder_id, call.value.shareholder_type),
    investor_set: entityStore.entity,
    invoice: invoice.value,
  } as CallExpanded
})
const invoice_status = computed(() => props.status || invoice.value?.status || null)

const updateURLQuery = () => {
  const query = {
    ...route.query,
    call_id: props.callId,
  }
  const queryString = createQueryString(query)
  window.history.replaceState(null, null, `?${queryString}`)
}

const modal = useModal()

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

const enable_links = true

hideSkeleton()

const refetch = async () => {
  if (props.refetch) await props.refetch()
}

const fetchAndOpenModal = async () => {
  showSkeleton()
  modal.open()
  updateURLQuery()
  call.value = await store.fetchCall(props.callId, { slug })
  if (call.value.invoice_id) {
    invoice.value = await invoiceStore.fetchInvoice(call.value.invoice_id, { slug })
  }
  if (props.onOpenDetails) await props.onOpenDetails()
  hideSkeleton()
}

const generateInvoice = async () => {
  if (!expandedCall.value) return
  if (invoice.value?.id) return
  await store.generateInvoice(props.callId, { slug })
  await refetch()
}

// -----------------------------------------------------------------------------
// Charts
// -----------------------------------------------------------------------------

const capitalAmount = computed(() => toNumber(expandedCall.value?.capital ?? initialMoney))
const currency = computed(() => entityStore.entity?.currency || 'USD')

const capitalCallTotalValue = computed(() => {
  if (Number.isNaN(capitalAmount.value)) return '--'
  return formatCurrency(capitalAmount.value, { currency: currency.value, decimals: 0 })
})

const capitalCallSeries = computed(() => [
  { name: 'Capital', value: capitalAmount.value, color: '#3982AF', textColor: '#ffffff' },
])

const getDescriptionListItems = computed(() => {
  const nValue = (val: any) => n(val, 'currency') || '--'

  const common = [
    { label: 'Capital', value: nValue(invoice.value?.total_amount || expandedCall.value?.capital), key: 'capital' },
    { label: 'Investor', value: expandedCall.value?.shareholder?.name, key: 'investor' },
    { label: 'Due on', value: format(expandedCall.value?.due_date, 'date'), key: 'date' },
  ]

  if (expandedCall.value.invoice_id) {
    const extra = [
      { label: 'Status', value: format(invoice.value?.marked_as_paid, 'boolean'), key: 'marked_as_paid' },
      { label: 'Paid Date', value: format(invoice.value?.paid_at, 'date'), key: 'paid_at' },
    ]
    return [...common, ...extra]
  }

  return common
})
</script>

<template>
  <template v-if="$slots.default">
    <slot :fetchAndOpenModal="fetchAndOpenModal"></slot>
  </template>
  <div v-else class="flex justify-center">
    <button v-if="invoice_status" @click="fetchAndOpenModal" class="relative" :disabled="skeleton">
      <div class="relative" :class="skeleton ? 'opacity-50' : ''">
        <template v-if="$slots.label">
          <slot name="label"></slot>
        </template>
        <template v-else>
          <InvoiceBadge class="cursor-pointer" :status="invoice_status" />
        </template>
      </div>

      <template v-if="skeleton">
        <div class="absolute inset-0 flex items-center justify-center">
          <VLoading />
        </div>
      </template>
    </button>
    <VBadge v-else-if="isScheduled" color="V_UNPAID" :dot="false">Pending</VBadge>
    <VButton v-else-if="isAdmin" :click="generateInvoice" size="xs">Generate Invoice</VButton>
  </div>
  <VModal :modalStore="modal" :has_footer="false">
    <template #main>
      <div class="flex flex-col space-y-6">
        <div>
          <StackedChartSummary
            :currency="currency"
            :loading="skeleton"
            :series="capitalCallSeries"
            title="Capital Call Summary"
            totalLabel="Called"
            :totalValue="capitalCallTotalValue"
          />
        </div>
        <template v-if="isAdmin && !invoice_status && !isScheduled">
          <VButton :click="generateInvoice" class="w-full">Generate Invoice</VButton>
        </template>
        <div>
          <DescriptionList
            :items="getDescriptionListItems"
            :loading="skeleton"
            :title="invoice.id ? `Invoice Details` : 'Details'"
          >
            <template #marked_as_paid>
              <VBadge color="V_PAID" :dot="false" v-if="invoice.marked_as_paid"> Paid </VBadge>
              <VBadge color="V_UNPAID" :dot="false" v-else>Unpaid</VBadge>
            </template>

            <template #investor>
              <RouterLink v-if="enable_links" class="hyperlink" :to="investorPath(expandedCall?.shareholder?.cid)">
                {{ expandedCall?.shareholder?.name }}
              </RouterLink>
              <span v-else>{{ expandedCall?.shareholder?.name }}</span>
            </template>
          </DescriptionList>
        </div>
        <CallPaymentForm v-if="isAdmin && invoice_status" :invoice="invoice" :refetch="refetch" />

        <div ref="wiringInstructionsSection">
          <DescriptionList
            v-if="invoice?.wiring_instructions"
            :items="[{ label: invoice.wiring_instructions, value: '', key: 'wiring_instructions' }]"
            :loading="skeleton"
            title="Wiring Instructions"
            class="transition duration-500"
            :class="{ 'scale-105 !border-[#3b88af]': focusedOn === 'wiring-instructions' }"
          />
        </div>

        <div>
          <DescriptionList
            v-if="invoice?.notes"
            :items="[{ label: invoice.notes, value: '', key: 'notes' }]"
            :loading="skeleton"
            title="Notes"
          >
          </DescriptionList>
        </div>
      </div>
    </template>
  </VModal>
</template>
